import React from 'react';
import { Eye, EyeOff, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export default function EditorSectionHeader({
  title,
  icon: IconComponent,
  percentage = 0,
  errors = [],
  warnings = [],
  completed = false,
  visible = true,
  isExpanded = false,
  isCollapsible = true,
  canMoveUp = false,
  canMoveDown = false,
  onToggle = () => {},
  onToggleVisibility = () => {},
  onDisable = () => {},
  onMoveUp = () => {},
  onMoveDown = () => {},
  capabilities = {},
  theme
}) {
  const isDark = theme === 'dark';

  const statusIndicator = () => {
    if (errors?.length > 0) {
      return (
        <span
          style={{
            fontSize: '11px',
            color: '#EF4444',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
            padding: '2px 8px',
            borderRadius: '12px',
          }}
        >
          ✕ {errors.length}
        </span>
      );
    }
    if (warnings?.length > 0) {
      return (
        <span
          style={{
            fontSize: '11px',
            color: '#F59E0B',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: isDark ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
            padding: '2px 8px',
            borderRadius: '12px',
          }}
        >
          ⚠️ {warnings.length}
        </span>
      );
    }
    if (completed) {
      return (
        <span
          style={{
            fontSize: '11px',
            color: '#10B981',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#D1FAE5',
            padding: '2px 8px',
            borderRadius: '12px',
          }}
        >
          ✓ Complete
        </span>
      );
    }
    return null;
  };

  return (
    <div
      onClick={isCollapsible ? onToggle : undefined}
      className="px-3 py-3 md:px-5 md:py-4"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: isExpanded 
          ? (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)') 
          : 'transparent',
        borderBottom: isExpanded ? '1px solid var(--divider)' : 'none',
        cursor: isCollapsible ? 'pointer' : 'default',
        userSelect: 'none',
        boxSizing: 'border-box',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={e => {
        if (!isExpanded && isCollapsible) {
          e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.005)';
        }
      }}
      onMouseLeave={e => {
        if (!isExpanded && isCollapsible) {
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {/* Left side: Icon + Title + Progress Badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
        {IconComponent && (
          <div style={{ color: 'var(--brand)', display: 'flex', flexShrink: 0 }}>
            <IconComponent size={16} strokeWidth={2.2} />
          </div>
        )}
        <span
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '10px',
            background: isDark ? '#1F1F24' : '#F3F4F6',
            color: 'var(--text-secondary)',
            flexShrink: 0,
          }}
        >
          {percentage}%
        </span>
      </div>

      {/* Right side: Chevron dropdown button + action keys */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}
        onClick={e => e.stopPropagation()} // Prevent trigger header collapse on button clicks
      >
        {statusIndicator()}

        {/* Chevron Dropdown Arrow - Styled like Canva/ChatGPT on the right */}
        {isCollapsible && (
          <button
            onClick={onToggle}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              color: 'var(--text-secondary)',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              outline: 'none',
            }}
          >
            <ChevronDown size={16} strokeWidth={2.5} />
          </button>
        )}

        {/* Section Actions (Move, Hide, Delete) */}
        <div 
          className="hidden sm:flex"
          style={{ alignItems: 'center', gap: '6px', borderLeft: '1px solid var(--divider)', paddingLeft: '10px' }}
        >
          {/* Ordering arrows */}
          {capabilities.reorder && (
            <div style={{ display: 'flex', gap: 2 }}>
              <button
                onClick={onMoveUp}
                disabled={!canMoveUp}
                title="Move Up"
                style={{
                  background: 'transparent', border: 'none', padding: 4, borderRadius: 4,
                  cursor: canMoveUp ? 'pointer' : 'not-allowed', display: 'flex',
                  color: canMoveUp ? 'var(--text-secondary)' : 'var(--text-muted)', opacity: canMoveUp ? 0.8 : 0.3,
                  outline: 'none',
                }}
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={onMoveDown}
                disabled={!canMoveDown}
                title="Move Down"
                style={{
                  background: 'transparent', border: 'none', padding: 4, borderRadius: 4,
                  cursor: canMoveDown ? 'pointer' : 'not-allowed', display: 'flex',
                  color: canMoveDown ? 'var(--text-secondary)' : 'var(--text-muted)', opacity: canMoveDown ? 0.8 : 0.3,
                  outline: 'none',
                }}
              >
                <ChevronDown size={14} />
              </button>
            </div>
          )}

          {/* Visibility Hide Toggle */}
          {capabilities.hideable !== false && (
            <button
              onClick={onToggleVisibility}
              title={visible ? "Hide from preview/export" : "Show on preview/export"}
              style={{
                background: 'transparent', border: 'none', padding: 4, borderRadius: 4,
                cursor: 'pointer', display: 'flex',
                color: visible ? 'var(--brand)' : 'var(--text-muted)', opacity: 0.8,
                outline: 'none',
              }}
            >
              {visible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          )}

          {/* Soft-delete / Remove button */}
          {capabilities.deletable !== false && (
            <button
              onClick={onDisable}
              title="Remove Section"
              style={{
                background: 'transparent', border: 'none', padding: 4, borderRadius: 4,
                cursor: 'pointer', display: 'flex',
                color: '#EF4444', opacity: 0.7,
                outline: 'none',
              }}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
